import { execute } from '@/graphql/execute';
import type { LayerStackLayerMeta } from '@/lib/layerStack/layerMeta';
import type {
  SectionOverlays,
  SectionOverlaysJson,
  SectionOverlaysLayerMeta,
  StackResult,
  StationOffsets,
  StationSections,
  StationVolumesJson,
  StationVolumesMeta,
} from '@/lib/stationVolumes/types';
import { STATION_VOLUMES_SCHEMA_VERSION } from '@/lib/stationVolumes/types';
import { isLowCoverage } from '@/lib/stationVolumes/coverageBands';
import { batchByproductsQuery } from '@/queries/byproduct.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

export interface FetchProgress {
  loadedBytes: number;
  totalBytes: number | null;
  phase: 'fetching' | 'decoding';
}

/**
 * There is no module-level cache here.
 *
 * There used to be one — a `Map` keyed by byproduct filename, with a
 * `__clearStationOffsetCache` export "cleared on project change by the
 * Viewer". Nothing ever called it, so decoded `.f32` planes (tens of MB
 * apiece) survived every project switch for the life of the tab. It also
 * bought nothing: `useStationOffsets` below is a react-query query with
 * `staleTime: Infinity` and `gcTime: Infinity` keyed by the same filename,
 * so the decode already survives dock close/open and tab switches, and the
 * query cache is emptied with the rest of the client when the app tears down.
 *
 * If a real eviction policy is ever needed, it belongs on the QueryClient
 * (a finite `gcTime`, or `queryClient.removeQueries`), not in a second cache
 * that nobody can see from the component tree.
 */

export function decodeStationOffsets(
  buf: ArrayBuffer,
  meta: { nStations: number; nOffsets: number; offsetStartM: number; offsetStepM: number },
): StationOffsets {
  const per = meta.nStations * meta.nOffsets;
  const expected = per * 3 * 4;
  if (buf.byteLength !== expected) {
    throw new Error(
      `station_offsets.f32 is ${buf.byteLength} bytes, expected ${expected} for ${meta.nStations}x${meta.nOffsets}x3`,
    );
  }
  return {
    dz: new Float32Array(buf, 0, per),
    zTop: new Float32Array(buf, per * 4, per),
    zBottom: new Float32Array(buf, per * 8, per),
    nStations: meta.nStations,
    nOffsets: meta.nOffsets,
    offsetStartM: meta.offsetStartM,
    offsetStepM: meta.offsetStepM,
  };
}

/**
 * One station's section, as three views into the already-decoded planes.
 * No allocation per frame and no interpolation between bins — a section is
 * *of* a bin, and pretending otherwise invents geometry (spec §6).
 */
export function sectionRowAt(offsets: StationOffsets, binIndex: number) {
  const i = Math.max(0, Math.min(offsets.nStations - 1, binIndex));
  const a = i * offsets.nOffsets;
  const b = a + offsets.nOffsets;
  return {
    dz: offsets.dz.subarray(a, b),
    zTop: offsets.zTop.subarray(a, b),
    zBottom: offsets.zBottom.subarray(a, b),
  };
}

export function decodeStationSections(
  buf: ArrayBuffer,
  meta: {
    nStationLines: number;
    nOffsets: number;
    offsetStartM: number;
    offsetStepM: number;
    planes: string[];
  },
): StationSections {
  const per = meta.nStationLines * meta.nOffsets;
  const nPlanes = meta.planes.length;
  // Blame the META, not the file. Both guards below are about the name list
  // alone — a byte-length check cannot catch either, because a 1-plane meta
  // with a 1-plane file and a `['zTop','zTop']` meta with a 2-plane file both
  // have exactly the length they declare.
  if (nPlanes < 2) {
    throw new Error(
      `station_sections.f32 metadata names ${nPlanes} plane(s) (${meta.planes.join(', ')}); ` +
        `at least 2 are required (the zTop/zBottom as-built pair)`,
    );
  }
  const duplicate = meta.planes.find((name, i) => meta.planes.indexOf(name) !== i);
  if (duplicate !== undefined) {
    // A `Record<string, Float32Array>` would silently collapse the pair,
    // rendering one layer's geometry twice with nothing complaining. The
    // writer rejects this too (`scripts/station_bins.py`).
    throw new Error(
      `station_sections.f32 metadata repeats the plane name "${duplicate}" (${meta.planes.join(', ')}); ` +
        `plane names must be unique`,
    );
  }
  const expected = per * nPlanes * 4;
  if (buf.byteLength !== expected) {
    throw new Error(
      `station_sections.f32 is ${buf.byteLength} bytes, expected ${expected} for ` +
        `${meta.nStationLines}x${meta.nOffsets}x${nPlanes} (${meta.planes.join(', ')})`,
    );
  }
  const planes: Record<string, Float32Array> = {};
  meta.planes.forEach((name, i) => {
    planes[name] = new Float32Array(buf, i * per * 4, per);
  });
  return {
    planes,
    planeNames: meta.planes,
    // Kept so every existing caller is untouched. A stack file still has
    // these two as its first as-built pair.
    zTop: planes[meta.planes[0]],
    zBottom: planes[meta.planes[1]],
    nStationLines: meta.nStationLines,
    nOffsets: meta.nOffsets,
    offsetStartM: meta.offsetStartM,
    offsetStepM: meta.offsetStepM,
  };
}

/**
 * One station LINE's section, as two views into the already-decoded planes.
 * There is no `dz` plane: unlike the bin means, `dz` here is exactly
 * `zTop − zBottom` with no averaging, so storing it would be redundant bytes.
 *
 * Clamps, like `sectionRowAt` above — and deliberately unlike the server twin
 * in `processReport/stationOffsets.ts`, which throws. A UI that clamps keeps
 * drawing; a report that clamps prints a page for a station it does not have.
 */
export function sectionLineAt(sections: StationSections, k: number) {
  const i = Math.max(0, Math.min(sections.nStationLines - 1, k));
  const a = i * sections.nOffsets;
  const b = a + sections.nOffsets;
  return { zTop: sections.zTop.subarray(a, b), zBottom: sections.zBottom.subarray(a, b) };
}

interface ByproductUrlResult {
  url: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Resolve a registered byproduct filename to its signed download URL via the
 * batch query. T8.1: matches `b.filename === filename` exactly — the
 * station-volumes metadata only ever carries registered byproduct filenames
 * (e.g. `<stem>_stations.json`), never S3 keys, so there is nothing to derive
 * a basename from.
 *
 * Surfaces the batch query's own loading/error state instead of collapsing
 * everything to a plain `string | null`: a failed `batchByproductsQuery`, an
 * in-flight one, and "the batch resolved but no byproduct with this filename
 * was registered" are three different situations a consumer needs to render
 * differently, not one silent `null`.
 *
 * A MISS IS A STALE CACHE UNTIL PROVEN OTHERWISE.
 *
 * The filename comes from the layer's metadata and the list from the batch,
 * and those are two independently cached queries that a run moves at the same
 * instant: `SECTION_OVERLAYS` registers a new pair and deletes the superseded
 * one in the same breath as it stamps `metadata.sectionOverlays`. Whichever of
 * the two queries refreshes second, there is a window where the pointer names
 * a file the cached list does not carry — before registration if the list is
 * old, after deletion if the pointer is. Reporting that window straight to the
 * user is what put "Could not load the section overlays" on the chart right
 * after a re-sample, and nothing refetched afterwards to take it back: the
 * dock's polling stops the moment the job resolves.
 *
 * So a miss costs ONE recovery pass — re-read the list, and refresh the
 * `layers` query that holds the pointer — reported as loading, not failure.
 * Only a miss that survives its own recovery is an error. One pass per
 * (batch, filename): a pointer that is genuinely dead still fails, and fails
 * once, rather than spinning the two queries against each other.
 */
const useByproductUrl = (batchId: string | undefined, filename: string | null | undefined): ByproductUrlResult => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['batchByproducts', batchId],
    queryFn: () => execute(batchByproductsQuery, { id: batchId! }),
    enabled: !!batchId && !!filename,
    staleTime: 5 * 60_000,
  });
  const settled = !query.isLoading && !query.isFetching && !query.error && !!query.data;
  const match = filename && settled ? query.data.batch?.byproducts?.find((b) => b.filename === filename) : undefined;
  /** The (batch, filename) pair whose recovery pass has already been spent.
   *  State rather than a ref because it is READ during render, which under
   *  the React Compiler a ref cannot be. */
  const [recovered, setRecovered] = useState<string | null>(null);
  const missKey = settled && filename && !match ? `${batchId ?? ''}|${filename}` : null;
  const recovering = !!missKey && recovered !== missKey;
  const { refetch } = query;
  useEffect(() => {
    if (!missKey || recovered === missKey) return;
    setRecovered(missKey);
    void refetch();
    // The pointer itself lives on the layer row. `['layers']` is the prefix
    // of the per-project key (`['layers', projectId]`) the viewer loads it
    // through, and this hook has no project id of its own.
    void queryClient.invalidateQueries({ queryKey: ['layers'] });
  }, [missKey, recovered, refetch, queryClient]);
  if (!filename) return { url: null, isLoading: false, error: null };
  if (query.error) return { url: null, isLoading: false, error: query.error as Error };
  if (query.isLoading || query.isFetching) return { url: null, isLoading: true, error: null };
  if (!query.data) return { url: null, isLoading: false, error: null };
  if (!match) {
    if (recovering) return { url: null, isLoading: true, error: null };
    return { url: null, isLoading: false, error: new Error(`No byproduct registered for filename "${filename}"`) };
  }
  return { url: match.downloadUrl ?? null, isLoading: false, error: null };
};

/** Streamed fetch that reports Content-Length progress into `onProgress`. */
async function fetchWithProgress(url: string, onProgress: (p: FetchProgress) => void): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const total = Number(res.headers.get('content-length')) || null;
  if (!res.body) {
    onProgress({ loadedBytes: 0, totalBytes: total, phase: 'decoding' });
    return await res.arrayBuffer();
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress({ loadedBytes: loaded, totalBytes: total, phase: 'fetching' });
  }
  onProgress({ loadedBytes: loaded, totalBytes: total, phase: 'decoding' });
  const out = new Uint8Array(loaded);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  return out.buffer;
}

/**
 * A station the reference surface never reaches is uncovered alignment, so it
 * is low coverage too (`isLowCoverage`). Applied once, on read, so the chart,
 * table, strip map, 3D bands and counts all agree — and a run made before the
 * worker flagged such stations reads the same as one made after.
 */
export function withEmptyStationsLow(json: StationVolumesJson): StationVolumesJson {
  const min = json.params.minCoveragePct;
  if (!json.bins.some((b) => !b.lowCoverage && isLowCoverage(b, min))) return json;
  return { ...json, bins: json.bins.map((b) => (isLowCoverage(b, min) ? { ...b, lowCoverage: true } : b)) };
}

export function useStationVolumes(layerId: string | null, meta: StationVolumesMeta | null, batchId?: string) {
  const [progress, setProgress] = useState<FetchProgress | null>(null);
  const byproduct = useByproductUrl(batchId, meta?.byproducts?.json);
  const query = useQuery({
    // Keyed by the byproduct filename, not the layer: the object is
    // immutable, so the result is safe to keep for the whole session.
    queryKey: ['stationVolumes', meta?.byproducts?.json ?? layerId],
    enabled: !!byproduct.url,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<StationVolumesJson> => {
      try {
        const buf = await fetchWithProgress(byproduct.url!, setProgress);
        const json = JSON.parse(new TextDecoder().decode(buf)) as StationVolumesJson;
        if ((json.schemaVersion ?? 1) > STATION_VOLUMES_SCHEMA_VERSION) {
          throw new Error('SCHEMA_TOO_NEW');
        }
        return withEmptyStationsLow(json);
      } finally {
        // Always clear progress on the way out, success or failure, so a
        // failed fetch doesn't leave `phase: 'decoding'` stuck alongside the
        // error (reviewer minor #5).
        setProgress(null);
      }
    },
  });
  const { refetch } = query;
  return {
    data: query.data ?? null,
    isLoading: byproduct.isLoading || query.isLoading || query.isFetching,
    error: byproduct.error ?? (query.error as Error) ?? null,
    progress,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}

/**
 * Is `meta.params.offsetBinM` a usable column width?
 *
 * Both `.f32` byproducts are decoded against an offset axis of
 * `offsetStartM + (j + 0.5) * offsetStepM`, and the client takes that step
 * from `meta.params` (the server twin takes the other route on purpose — see
 * CLAUDE.md). A non-positive or non-finite step is not a narrow section, it is
 * NO section: every column collapses onto offset 0 and the chart draws a
 * corridor of no width with nothing on screen saying so. Refuse the fetch
 * instead, and let the Section tab report that it has nothing.
 *
 * Reachable only through a synthesised meta — `station_bins.py` writes the
 * clamped `off_step` into `params.offsetBinM` on every real run — which is
 * exactly why the guard lives here, at the one place that spends the bytes,
 * rather than in each synthesiser.
 */
const hasOffsetAxis = (meta: StationVolumesMeta | null): boolean =>
  !!meta && Number.isFinite(meta.params.offsetBinM) && meta.params.offsetBinM > 0;

/**
 * The offset axis a byproduct was WRITTEN against, taken from its own block.
 *
 * `station_bins.py::offset_axis` anchors the columns on the centreline, so the
 * origin is no longer `-swathLeftM`: 10 m of swath at a 1 m column starts at
 * -9.5 and puts a column on offset 0. Synthesising the axis from `params`
 * instead — which both of these hooks used to do — lands every column half a
 * bin away from where the chart, the DXF sheet and the PDF report draw it, and
 * nothing type-checks across the Python boundary to say so.
 *
 * The `??` fallbacks carry runs written before the block did: their axis
 * really was the swath edge, and their block really may lack these fields.
 */
const axisOf = (
  block: { offsetStartM?: number; offsetStepM?: number } | null | undefined,
  meta: StationVolumesMeta,
): { offsetStartM: number; offsetStepM: number } => ({
  offsetStartM: Number.isFinite(block?.offsetStartM) ? block!.offsetStartM! : -meta.params.swathLeftM,
  offsetStepM:
    Number.isFinite(block?.offsetStepM) && block!.offsetStepM! > 0 ? block!.offsetStepM! : meta.params.offsetBinM,
});

export function useStationOffsets(meta: StationVolumesMeta | null, batchId?: string, enabled = true) {
  const [progress, setProgress] = useState<FetchProgress | null>(null);
  const key = meta?.byproducts?.offsets ?? null;
  const byproduct = useByproductUrl(batchId, key);
  const query = useQuery({
    queryKey: ['stationOffsets', key],
    enabled: enabled && !!byproduct.url && !!meta?.offsetBins && hasOffsetAxis(meta),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<StationOffsets> => {
      try {
        const buf = await fetchWithProgress(byproduct.url!, setProgress);
        return decodeStationOffsets(buf, {
          nStations: meta!.offsetBins!.nStations,
          nOffsets: meta!.offsetBins!.nOffsets,
          ...axisOf(meta!.offsetBins, meta!),
        });
      } finally {
        setProgress(null);
      }
    },
  });
  // Same stable-identity `refetch` the JSON hook returns: spec 4g requires a
  // Retry action on a failed fetch, and the Section tab is the only place
  // that can offer one for the `.f32` arrays.
  const { refetch } = query;
  return {
    data: query.data ?? null,
    isLoading: byproduct.isLoading || query.isLoading || query.isFetching,
    error: byproduct.error ?? (query.error as Error) ?? null,
    progress,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}

/**
 * The TRUE station-line sections (`station_sections.f32`). Same shape and same
 * caching contract as `useStationOffsets`; disabled when the run predates them,
 * in which case the Section tab falls back to the bin-mean planes.
 *
 * Like `useStationOffsets`, the offset axis comes from the byproduct's own
 * block via `axisOf` — the same route the server twin takes.
 */
export function useStationSections(meta: StationVolumesMeta | null, batchId?: string, enabled = true) {
  const [progress, setProgress] = useState<FetchProgress | null>(null);
  const key = meta?.byproducts?.sections ?? null;
  const byproduct = useByproductUrl(batchId, key);
  const query = useQuery({
    queryKey: ['stationSections', key],
    enabled: enabled && !!byproduct.url && !!meta?.sections && hasOffsetAxis(meta),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<StationSections> => {
      try {
        const buf = await fetchWithProgress(byproduct.url!, setProgress);
        return decodeStationSections(buf, {
          nStationLines: meta!.sections!.nStationLines,
          nOffsets: meta!.sections!.nOffsets,
          ...axisOf(meta!.sections, meta!),
          // Metadata persisted before the multi-layer stack has no `planes`
          // at all — fall back to the two planes every such run actually has.
          planes: meta!.sections!.planes ?? ['zTop', 'zBottom'],
        });
      } finally {
        setProgress(null);
      }
    },
  });
  const { refetch } = query;
  return {
    data: query.data ?? null,
    isLoading: byproduct.isLoading || query.isLoading || query.isFetching,
    error: byproduct.error ?? (query.error as Error) ?? null,
    progress,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}

/**
 * The REFERENCE overlay planes (`station_section_overlays.f32`).
 *
 * One plane per configured overlay, in the JSON's own order, on the RUN's
 * offset grid — so a decoded plane is addressed with exactly the row index the
 * chart already computed for the run itself. Blame the sidecar rather than the
 * bytes: the plane COUNT is what sizes the decode, and a mismatch means the
 * two halves of the byproduct came from different samples.
 */
export function decodeSectionOverlays(buf: ArrayBuffer, json: SectionOverlaysJson): SectionOverlays {
  const per = json.nStationLines * json.nOffsets;
  // Blame the SIDECAR, not the bytes. A repeated id would silently collapse
  // two planes into one `Record` entry, drawing one overlay's geometry twice
  // with nothing complaining — the same guard `decodeStationSections` carries
  // for the same reason.
  const duplicate = json.planes.find((p, i) => json.planes.findIndex((q) => q.id === p.id) !== i);
  if (duplicate)
    throw new Error(
      `station_section_overlays.json repeats the plane id "${duplicate.id}" ` +
        `(${json.planes.map((p) => p.id).join(', ')}); plane ids must be unique`,
    );
  const expected = per * json.planes.length * 4;
  if (buf.byteLength !== expected)
    throw new Error(
      `station_section_overlays.f32 is ${buf.byteLength} bytes, expected ${expected} for ` +
        `${json.nStationLines}x${json.nOffsets}x${json.planes.length}`,
    );
  const planes: Record<string, Float32Array> = {};
  json.planes.forEach((p, i) => {
    planes[p.id] = new Float32Array(buf, i * per * 4, per);
  });
  return { planes, meta: json };
}

/**
 * The overlays byproduct pair, decoded. Same caching contract as its siblings
 * (keyed by the immutable byproduct filenames, kept for the session), and the
 * same `{ data, isLoading, error, progress, refetch }` shape.
 *
 * TWO byproducts, one query: the `.f32` cannot be sliced without the JSON's
 * plane list, so fetching them independently would only create a window where
 * one is present and the other is not. The offset axis comes from the sidecar
 * here — unlike `useStationOffsets`/`useStationSections`, which take it from
 * `meta.params` — because an overlays file is sampled ON the run's grid and
 * carries it, and re-deriving it would be a second source of truth.
 */
export function useSectionOverlays(meta: SectionOverlaysLayerMeta | null, batchId?: string, enabled = true) {
  const [progress, setProgress] = useState<FetchProgress | null>(null);
  const jsonKey = meta?.byproducts.json ?? null;
  const f32Key = meta?.byproducts.f32 ?? null;
  const jsonUrl = useByproductUrl(batchId, jsonKey);
  const f32Url = useByproductUrl(batchId, f32Key);
  const query = useQuery({
    queryKey: ['sectionOverlays', f32Key, jsonKey],
    enabled: enabled && !!jsonUrl.url && !!f32Url.url,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<SectionOverlays> => {
      try {
        // A signed-URL 403 or a 5xx returns a BODY, and `.json()` on an HTML
        // error page throws something that reads like a parse bug. Fail on the
        // status instead, with the status in the message.
        const res = await fetch(jsonUrl.url!);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = (await res.json()) as SectionOverlaysJson;
        const buf = await fetchWithProgress(f32Url.url!, setProgress);
        return decodeSectionOverlays(buf, json);
      } finally {
        setProgress(null);
      }
    },
  });
  const { refetch } = query;
  return {
    data: query.data ?? null,
    isLoading: jsonUrl.isLoading || f32Url.isLoading || query.isLoading || query.isFetching,
    error: jsonUrl.error ?? f32Url.error ?? (query.error as Error) ?? null,
    progress,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}

/**
 * The LAYER_STACK compliance document (`stack.json`).
 *
 * Lives here rather than in a hook of its own so it reuses `useByproductUrl`
 * and `fetchWithProgress` verbatim: the batch byproduct lookup, the
 * filename-not-S3-key contract and the streamed progress reporting are
 * exactly the same problem, and a second copy of them would drift.
 *
 * One document for the WHOLE run — every layer's verdicts, blocks and per-bin
 * arrays. Keyed by filename like its siblings (the object is immutable), so
 * moving between the chips in the compliance strip never refetches.
 */
export function useLayerStack(layerId: string | null, meta: LayerStackLayerMeta | null, batchId?: string) {
  const [progress, setProgress] = useState<FetchProgress | null>(null);
  const filename = meta?.byproducts?.stack ?? null;
  const byproduct = useByproductUrl(batchId, filename);
  const query = useQuery({
    queryKey: ['layerStack', filename ?? layerId],
    enabled: !!byproduct.url,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<StackResult> => {
      try {
        const buf = await fetchWithProgress(byproduct.url!, setProgress);
        return JSON.parse(new TextDecoder().decode(buf)) as StackResult;
      } finally {
        setProgress(null);
      }
    },
  });
  const { refetch } = query;
  return {
    data: query.data ?? null,
    isLoading: byproduct.isLoading || query.isLoading || query.isFetching,
    error: byproduct.error ?? (query.error as Error) ?? null,
    progress,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}
